import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { Box, Button } from '@mui/material';
import { Icon, SimpleModal, toast } from '@livable/saas-react-components';
import moment from 'moment';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { FilePreviewPlain } from 'src/components/shared/FilePreviewModal/plainIndex';
import { Needs } from 'src/components/UtilitySetupSection/parts';
import { BILL_PREVIEW, resolveBillPreview } from './billPreviewState';
import { getBillAutomationErrorList, getBillAutomationWarningList } from 'src/pages/NewBillingModule/ApplyBillingFull/BillAutomationExtraction';
import { isInvoiceNumberFixed } from 'src/pages/NewBillingModule/ApplyBillingFull/AppfolioPush';
import { toBillEditPayload } from './BillDetails/billEditUtils';
import useApplyBillingStyles from 'src/pages/NewBillingModule/ApplyBillingFull/styles';
import { useStore } from 'src/stores';
import { BILL_AUTOMATION_STATUS } from 'src/stores/billAutomation/uploadedBills';
import { AllocationSuggestionsCard, BillDetailsCard, BillEditHistoryCard, useBillDetailsStyles } from './BillDetails';
import { SectionHeading } from './BillDetails/SectionHeading';
import { ConfirmActionModal } from './BillDetails/ConfirmActionModal';
import { getServicePairs } from './apGlAccounts';
import { isHeldByAppfolioPush, resolveBillApPushInvoiceNumber } from './appfolioPushRow';
import { HELD_FOR_PLAN, countAllocations, getBillingHold, getDeleteBlockedReason, getRetryBlockedReason, getEditBlockedReason, getScheduledMonth } from './companyBillRows';
import { getDeleteConfirmation } from './archivedAllocationWording';
import { BillDownloadButton } from './BillDownloadButton';
import { billDownloadBlockedReason } from './billsDownload';
import { useApGlAccounts } from './useApGlAccounts';
import { useBillApPushDetails } from './useBillApPushDetails';
import { RetryBillModal } from './RetryBillModal';
import { BillNoteBlock } from './BillDetails/BillNoteBlock';
import { billStatusBannerCopy, isHeldForPlanReason } from './billStatusBanner';

/**
 * Read-only details of a bill uploaded from the Bills page: the attached file on the left, the
 * extraction result on the right (Figma: "Apply billing - default").
 */
export const UploadedBillDetailsModal = observer(props => {
  var _bill$handEntry;
  const appfolioPush = props.appfolioPush,
    bill = props.bill,
    canTriggerPush = props.canTriggerPush,
    handleClose = props.handleClose,
    isDownloading = props.isDownloading,
    listApPush = props.listApPush,
    _props$monthInProgres = props.monthInProgress,
    monthInProgress = _props$monthInProgres === void 0 ? null : _props$monthInProgres,
    onAttach = props.onAttach,
    onBillChanged = props.onBillChanged,
    onDownload = props.onDownload,
    onEnteredByHand = props.onEnteredByHand,
    onOpenBill = props.onOpenBill;
  const classes = useBillDetailsStyles();
  const bannerClasses = useApplyBillingStyles();
  const store = useStore();
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isConfirmingDelete = _useState2[0],
    setIsConfirmingDelete = _useState2[1];
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isConfirmingUseThisCycle = _useState4[0],
    setIsConfirmingUseThisCycle = _useState4[1];
  // Bumped to make the allocation card read where the bill's services stand again, after one moved month.
  const _useState5 = useState(0),
    _useState6 = _slicedToArray(_useState5, 2),
    allocationsVersion = _useState6[0],
    setAllocationsVersion = _useState6[1];
  const _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    isRetrying = _useState8[0],
    setIsRetrying = _useState8[1];
  /** The form a failed bill is entered by hand on is open (LB-6095), for this bill only. */
  const _useState9 = useState(null),
    _useState0 = _slicedToArray(_useState9, 2),
    enteringBillId = _useState0[0],
    setEnteringBillId = _useState0[1];
  /** A hand entry refused as a copy: the bill on record it copies, offered to open instead, for this bill only. */
  const _useState1 = useState(null),
    _useState10 = _slicedToArray(_useState1, 2),
    copyOnRecord = _useState10[0],
    setCopyOnRecord = _useState10[1];
  // The view stays mounted while it is closed, so a form left open is closed with it: reopening the bill shows its
  // banner again, not a draft from before.
  useEffect(() => {
    setEnteringBillId(null);
    setCopyOnRecord(null);
  }, [bill === null || bill === void 0 ? void 0 : bill.billId]);
  // Called before the early return below, because a hook cannot be conditional. It reads nothing
  // without a bill.
  const apPushDetails = useBillApPushDetails({
    billId: bill === null || bill === void 0 ? void 0 : bill.billId,
    isEnabled: !!bill && store.company.companyFeatureStore.isAppfolio,
    listApPush
  });
  /**
   * The GL codes flag gates the read as well as AppFolio: the service only fills the Accounts Payable
   * account in while that flag is on, so reading with it off would show every property as unconfigured
   * when the truth is that it was never sent.
   *
   * The push status comes in because a pushed bill answers a different question — where its payable
   * posted, not where one would post now.
   */
  const apGlAccounts = useApGlAccounts({
    isEnabled: !!bill && store.company.companyFeatureStore.isAppfolio && store.account.featureFlagsStore.getIsGlCodesEnabled,
    pushStatus: apPushDetails.pushStatus,
    // The bill as it would be pushed, edit included: the push builds its lines from the same payload.
    servicePairs: getServicePairs(bill === null || bill === void 0 ? void 0 : bill.effectiveData)
  });

  // The stored document, previewed from a short-lived presigned URL when there is no in-session
  // upload blob to show — i.e. bills that arrived by email, or any bill reopened after a reload
  // (LB-5755 C1). Fetched fresh on open because the URL expires (~15 min) and is opened in an iframe
  // below, never fetch()'d (the bill bucket has no CORS). status: 'loading' until resolved, then
  // 'ready' with a url, or 'none' when the bill has no stored document.
  // `fileName` as well as the url: the same response carries the name the document was stored
  // under, and for a bill this session did not upload it is the only name anywhere on the client
  // besides the poll (LB-5897). The viewer picks its renderer off that name, so without one it was
  // handed a PDF renderer and a JPEG.
  const _useState11 = useState({
      status: 'loading',
      url: '',
      fileName: ''
    }),
    _useState12 = _slicedToArray(_useState11, 2),
    sourcePreview = _useState12[0],
    setSourcePreview = _useState12[1];
  const billId = bill === null || bill === void 0 ? void 0 : bill.billId;
  const hasSessionBlob = !!(bill !== null && bill !== void 0 && bill.fileUrl);
  useEffect(() => {
    if (!billId || hasSessionBlob) {
      setSourcePreview({
        status: 'loading',
        url: '',
        fileName: ''
      });
      return undefined;
    }
    let active = true;
    setSourcePreview({
      status: 'loading',
      url: '',
      fileName: ''
    });
    (async (_resp$response, _resp$response2) => {
      const resp = await store.companyBills.getBillFileUrlAsync.run({
        billId
      });
      if (!active) return;
      const answered = resp && resp.isSuccessful !== false;
      const url = answered ? (_resp$response = resp.response) === null || _resp$response === void 0 ? void 0 : _resp$response.url : '';
      const storedName = answered && ((_resp$response2 = resp.response) === null || _resp$response2 === void 0 ? void 0 : _resp$response2.fileName) || '';
      setSourcePreview(url ? {
        status: 'ready',
        url,
        fileName: storedName
      } : {
        status: 'none',
        url: '',
        fileName: storedName
      });
    })();
    return () => {
      active = false;
    };
  }, [billId, hasSessionBlob, store.companyBills]);
  if (!bill) return null;
  const billResponse = {
    status: bill.status,
    data: bill.data,
    errors: [...(bill.errors || [])],
    warnings: [...(bill.warnings || [])],
    // Null from an older service, which then falls back to the two above (LB-5918).
    reasons: bill.reasons ? [...bill.reasons] : null,
    notices: bill.notices ? [...bill.notices] : null
  };
  const errorList = getBillAutomationErrorList(billResponse);
  const warningList = getBillAutomationWarningList(billResponse);
  const needsProperty = bill.status === BILL_AUTOMATION_STATUS.needsProperty;
  // A bill whose property could not be matched is still fully extracted, so its data is worth showing, beside
  // the reasons it waits, which such a bill always has (LB-5918). Its Edit is also the only way to give a bill
  // that prints no service period its dates, without which attaching it can never validate (LB-6007).
  const isExtractionReady = bill.status === BILL_AUTOMATION_STATUS.validated || needsProperty;
  const extractedBillData = isExtractionReady && bill.data && (errorList.length === 0 || needsProperty) ? bill.effectiveData : null;
  const isAppfolio = store.company.companyFeatureStore.isAppfolio;
  // Read once for the whole view: matching the bill against them is the same question in both cards.
  const properties = store.company.propertiesList;
  /**
   * The service answered 404: the bill was deleted, or the link names one that never existed.
   *
   * Every other state below is about a bill that exists, so this is asked first and answers the
   * whole view. Before it, a deleted bill followed a link to a modal that said "Processing your
   * bill" and went on saying it: polling had already given up, and nothing was left to move the
   * banner (LB-5897).
   */
  const isMissing = !!bill.isMissing;

  // Which of the document pane s five states applies, and in which order they are asked. See
  // billPreviewState: the order is the part that was wrong, so it lives somewhere it can be read
  // and tested rather than inside a chain of ternaries.
  const preview = resolveBillPreview({
    isMissing,
    sessionFileUrl: bill.fileUrl,
    sourceStatus: sourcePreview.status
  });
  const isProcessing = !isMissing && !bill.isFinalStatus;
  const isDuplicate = bill.status === BILL_AUTOMATION_STATUS.duplicate;
  const isRejected = bill.status === BILL_AUTOMATION_STATUS.rejected;
  // A failed bill whose read can be entered by hand (LB-6095), and the form for it while it is open.
  const isFailed = bill.status === BILL_AUTOMATION_STATUS.failed;
  const canEnterByHand = isFailed && ((_bill$handEntry = bill.handEntry) === null || _bill$handEntry === void 0 ? void 0 : _bill$handEntry.canEnter) === true;
  const isEnteringByHand = canEnterByHand && enteringBillId === bill.billId && !!bill.data;
  const handEntryNote = isFailed && bill.handEntry && !bill.handEntry.canEnter ? bill.handEntry.reason : null;
  const copyOnRecordId = isFailed && (copyOnRecord === null || copyOnRecord === void 0 ? void 0 : copyOnRecord.billId) === bill.billId ? copyOnRecord.copyBillId : null;
  // The poll payload carries neither the link nor the reason; the list row has both, and the modal
  // already reads it for other things.
  const listRow = isDuplicate || isRejected ? store.companyBills.getBill(bill.billId) : null;
  const duplicateOfBillId = isDuplicate && (listRow === null || listRow === void 0 ? void 0 : listRow.duplicateOfBillId) || null;
  // The reason normally arrives in the polled errors; when it has not (row opened before its poll,
  // or a message shape the error reader filters out), the row's statusReason is the same text.
  const reasonList = errorList.length > 0 ? errorList : listRow !== null && listRow !== void 0 && listRow.statusReason ? [listRow.statusReason] : [];
  /**
   * Emailed after a Pro company's grace period and held, unread, until it has Plus (LB-6233): the upgrade reads it, so
   * it isn't attached. The row says so; a bill opened by a link before the list has it, its stored reason does. Only with
   * billing options on, as the table shows it.
   */
  const isHeldForPlan = !!store.account.featureFlagsStore.getIsBillingOptionsEnabled && isRejected && ((listRow === null || listRow === void 0 ? void 0 : listRow.heldReason) === HELD_FOR_PLAN || isHeldForPlanReason(bill.errors));
  // From the stored reasons' codes. The row's statusReason is already worded for customers, with no code to read.
  const bannerCopy = billStatusBannerCopy({
    errors: bill.errors,
    isDuplicate,
    isHeldForPlan,
    isRejected,
    needsProperty
  });
  const showErrorBanner = !isProcessing && !isMissing && (bill.status === BILL_AUTOMATION_STATUS.failed || isDuplicate || isRejected || errorList.length > 0);
  const showWarningBanner = !isProcessing && !isMissing && warningList.length > 0;

  // The poll now carries the name for every bill, and the file-url answer carries it too. Either
  // will do; between them, only a bill with no stored document at all falls through to the label.
  const storedFileName = bill.fileName || sourcePreview.fileName || '';
  const fileName = storedFileName || 'Uploaded bill';

  // The list is what knows how far each service of the bill has got, and whether a payable exists for
  // it, so it is what says whether the bill can still be deleted. A bill the list has not caught up
  // with yet is left to the service.
  const listedBill = store.companyBills.getBill(bill.billId);
  // Services scheduled for a later month that hasn't opened (LB-6009), as the Bills page lists them.
  const scheduledServices = ((listedBill === null || listedBill === void 0 ? void 0 : listedBill.utilities) || []).filter(utility => getScheduledMonth(utility, monthInProgress));
  const scheduledMonths = [...new Set(scheduledServices.map(utility => moment(utility.scheduledFor, 'YYYY-MM-DD').format('MMMM YYYY')))];
  const allocationCount = countAllocations(listedBill === null || listedBill === void 0 ? void 0 : listedBill.utilities, listedBill === null || listedBill === void 0 ? void 0 : listedBill.allocation, listedBill === null || listedBill === void 0 ? void 0 : listedBill.allocationCount);
  const isAllocated = allocationCount > 0;
  const isPushed = isHeldByAppfolioPush(listedBill === null || listedBill === void 0 ? void 0 : listedBill.apPush);
  // Nothing to delete when the service says there is no such bill, and offering it would invite a
  // second 404 in answer to a button press.
  const _getBillingHold = getBillingHold(listedBill === null || listedBill === void 0 ? void 0 : listedBill.utilities, listedBill),
    isBilled = _getBillingHold.isBilled,
    isClosed = _getBillingHold.isClosed;
  const canDelete = !isMissing && !isPushed && !isBilled && !isClosed;
  const deleteBlockedReason = getDeleteBlockedReason({
    isBilled,
    isClosed,
    isPushed
  });
  // Same two facts, and deliberately the same two: an edit guard that disagreed with the delete guard
  // about whether a bill is committed would be worse than no guard at all.
  const editBlockedReason = getEditBlockedReason({
    isAllocated,
    isPushed
  });
  // The row carries the same gate the table's menu uses; the Retry dialog asks the service for the rest.
  // A bill opened by deep link or "view the original" may not be in the loaded page yet — then nothing
  // here knows its status, and the gate is left to the service, as delete's is.
  const retryBlockedReason = isMissing ? 'This bill is no longer available' : listedBill ? getRetryBlockedReason({
    isBilled,
    isClosed,
    isPushed,
    extractStatus: listedBill.extractStatus
  }) : null;
  const downloadBlockedReason = isMissing ? 'This bill is no longer available' : billDownloadBlockedReason((listedBill === null || listedBill === void 0 ? void 0 : listedBill.extractStatus) || bill.status, isHeldForPlan);

  /** A retried bill is archived (LB-5787): it leaves the list, and this drawer closes on it. */
  const handleRetried = () => {
    setIsRetrying(false);
    store.uploadedBills.removeUploadedBill(bill.billId);
    handleClose();
    store.companyBills.refetchCurrent();
  };

  /** "Use it this cycle" (LB-6009): the bill's services are filed under the month in progress. */
  const handleUseThisCycle = async () => {
    setIsConfirmingUseThisCycle(false);

    // Only the scheduled services, named: the service moves exactly those, and checks each one's property.
    const resp = await store.companyBills.useBillThisCycleAsync.run({
      billId: bill.billId,
      services: scheduledServices.map(_ref => {
        let propertyId = _ref.propertyId,
          utilityId = _ref.utilityId;
        return {
          propertyId,
          utilityId
        };
      })
    });
    if (!resp || !resp.isSuccessful) {
      toast('error', {
        title: 'Bill not moved to this cycle',
        body: [(resp === null || resp === void 0 ? void 0 : resp.message) || 'This bill could not be moved to this cycle. Please try again.']
      });
      return;
    }
    toast('success', {
      body: ["".concat(fileName, " will be used this cycle.")]
    });
    store.companyBills.refetchCurrent();
    setAllocationsVersion(version => version + 1);
  };
  const handleDelete = async () => {
    setIsConfirmingDelete(false);
    const resp = await store.companyBills.deleteBillAsync.run({
      billId: bill.billId
    });
    if (!resp) return;
    if (!resp.isSuccessful) {
      toast('error', {
        title: 'Bill not deleted',
        body: [resp.message || 'This bill can no longer be deleted.']
      });
      return;
    }
    store.uploadedBills.removeUploadedBill(bill.billId);
    handleClose();
    toast('success', {
      body: ["".concat(fileName, " deleted.")]
    });
  };

  /**
   * Persist the correction (LB-5840). A refusal arrives as a resolved answer carrying the service's
   * own wording — the bill was allocated or pushed while this sat open, or somebody else changed it
   * first — so it is shown here and `false` leaves the draft open rather than discarding the work
   * the refusal is about.
   */
  const handleSaveBillData = async data => {
    const resp = await store.uploadedBills.saveBillEditAsync.run({
      billId: bill.billId,
      data: toBillEditPayload(data),
      version: bill.version
    });

    // Nothing came back: the request never landed, and the async model has already said so.
    if (!resp) return false;
    if (!resp.isSuccessful) {
      toast('error', {
        title: 'Bill not saved',
        body: [resp.message || 'This bill has changed since you opened it. Reopen it and try again.']
      });
      return false;
    }

    // It landed, stale view or not, so the row behind has something new to show (LB-5979).
    onBillChanged === null || onBillChanged === void 0 || onBillChanged();
    if (resp.isStale) {
      // Saved, but the bill could not be read back, so what is on screen is the payload from before
      // the edit. Reopening the editor on that and saving would undo the change that just landed,
      // which is why this says so rather than reporting a plain success.
      toast('warning', {
        title: 'Saved, but this view is out of date',
        body: ['Your change was saved. Reopen the bill before editing it again.']
      });
      return true;
    }
    toast('success', {
      body: ['Bill details saved.']
    });
    return true;
  };

  /**
   * Send a failed bill on as entered by hand (LB-6095). The same save as an edit; the service moves the bill on to be
   * checked as typed, so the details are read again and the list, the tiles and the bill's own poll pick it up.
   *
   * Held for staff on its account, or not sent on, the entry was saved all the same: the form closes on the bill as it
   * now is, and says so rather than "not entered". Refused as a copy, the form stays open and the copy is offered.
   */
  const handleEnterByHand = async data => {
    const resp = await store.uploadedBills.saveBillEditAsync.run({
      billId: bill.billId,
      data: toBillEditPayload(data),
      version: bill.version
    });
    if (!resp) return false;

    // Whatever this save answers, a copy named by an earlier one is no longer the question.
    setCopyOnRecord(resp.copyBillId ? {
      billId: bill.billId,
      copyBillId: resp.copyBillId
    } : null);
    if (resp.outcome === 'Held' || resp.outcome === 'NotSent') {
      setEnteringBillId(null);
      onBillChanged === null || onBillChanged === void 0 || onBillChanged();
      toast('warning', {
        title: resp.outcome === 'Held' ? 'Saved, and held for Livable staff' : 'Saved, but not sent on',
        body: [resp.message || 'What you entered was saved.',
        // The bill could not be read back, so what is on screen is from before the save: entering it again from that
        // would be refused as stale, and would start from the read rather than what was typed.
        ...(resp.isStale ? ['Reopen the bill before entering it again.'] : [])]
      });
      return true;
    }
    if (!resp.isSuccessful) {
      toast('error', {
        title: 'Bill not entered',
        body: [resp.message || 'This bill has changed since you opened it. Reopen it and try again.']
      });
      return false;
    }
    setEnteringBillId(null);
    onEnteredByHand === null || onEnteredByHand === void 0 || onEnteredByHand();
    toast('success', {
      body: ["Bill entered. We're checking it now."]
    });
    return true;
  };

  /**
   * Throw every edit away and go back to what was read from the document.
   *
   * A different verb from the edit form's Cancel, and deliberately in a different place: Cancel
   * abandons the draft in hand, this discards what was already saved. Refused for the same reasons
   * a save is, and reported the same way.
   */
  const handleRestoreBillData = async () => {
    const resp = await store.uploadedBills.discardBillEditAsync.run({
      billId: bill.billId,
      version: bill.version
    });
    if (!resp) return;
    if (!resp.isSuccessful) {
      toast('error', {
        title: 'Bill not restored',
        body: [resp.message || 'This bill has changed since you opened it. Reopen it and try again.']
      });
      return;
    }
    onBillChanged === null || onBillChanged === void 0 || onBillChanged();
    if (resp.isStale) {
      toast('warning', {
        title: 'Restored, but this view is out of date',
        body: ['The original was restored. Reopen the bill to see it.']
      });
      return;
    }
    toast('success', {
      body: ['The original bill has been restored.']
    });
  };
  const handleSaveAllocationSettings = settings => store.uploadedBills.setAllocationSettings(bill.billId, settings);

  /**
   * The raw extraction says what OCR read and how confidently; the edit table is where a person can put
   * an invoice number the bill has none of. Judged apart, because `effectiveData` folds the two together
   * and a typed number must not be mistaken for a confidently extracted one.
   */
  const apPushInvoiceNumber = resolveBillApPushInvoiceNumber(bill);

  /**
   * Gated on AppFolio as well as on confidence: without a push there is nothing that would ignore an
   * edit here, so locking the field would take something away for no reason a person could act on.
   */
  const isInvoiceNumberLocked = isAppfolio && isInvoiceNumberFixed(bill.data);

  // Read for this bill alone: the list poll rests while this view is open, and the blockers behind a
  // failure live on the attempt, which the list row does not carry.
  const pushState = apPushDetails.getState(bill.status, apPushInvoiceNumber);
  return /*#__PURE__*/React.createElement(SimpleModal, {
    handleClose: handleClose,
    isOpen: !!bill
    // Keeps the edit fields and the file preview mounted while the store updates around them.
    ,
    isMemoChildren: true,
    noFooter: true,
    noHeader: true,
    noPadding: true,
    variant: "flex"
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.root
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.filePane
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.filePaneInner
  }, preview === BILL_PREVIEW.missing ?
  /*#__PURE__*/
  // Not "no document available", which says the bill is here and its file is not.
  React.createElement("div", {
    className: classes.filePlaceholder
  }, "This bill is no longer available.") : preview === BILL_PREVIEW.sessionBlob ? /*#__PURE__*/React.createElement("div", {
    className: classes.filePage
  }, /*#__PURE__*/React.createElement(FilePreviewPlain, {
    docUri: bill.fileUrl,
    fileId: "",
    fileName: storedFileName,
    fileSize: bill.fileSizeMB,
    isDisabledParent: true,
    isPreviewOnly: true,
    key: bill.fileUrl
  })) : preview === BILL_PREVIEW.stored ?
  /*#__PURE__*/
  // The same viewer as the in-session blob above, now that the bill bucket allows the
  // origin. It used to be an iframe, which handed the PDF to the browser's built-in
  // viewer — a force-installed extension, and having one on the page blocks DevTools for
  // the whole tab on a managed profile (LB-5822).
  React.createElement("div", {
    className: classes.filePage
  }, /*#__PURE__*/React.createElement(FilePreviewPlain, {
    docUri: sourcePreview.url,
    fileId: "",
    fileName: storedFileName,
    isDisabledParent: true,
    isPreviewOnly: true,
    key: sourcePreview.url
  })) : preview === BILL_PREVIEW.noDocument ? /*#__PURE__*/React.createElement("div", {
    className: classes.filePlaceholder
  }, "No document is available to preview for this bill.") : /*#__PURE__*/React.createElement("div", {
    className: classes.filePlaceholder
  }, "Loading the bill document\u2026"))), /*#__PURE__*/React.createElement("div", {
    className: classes.dataPane
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.dataHeader
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.fileCard
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.fileCardTitle
  }, /*#__PURE__*/React.createElement(Icon, {
    state: "grey",
    type: "file-text"
  }), /*#__PURE__*/React.createElement("span", {
    className: classes.fileCardName,
    title: fileName
  }, fileName)), onDownload ? /*#__PURE__*/React.createElement(BillDownloadButton, {
    className: classes.iconButton,
    disabledReason: downloadBlockedReason,
    isBusy: isDownloading,
    onPick: onDownload
  }) : null, /*#__PURE__*/React.createElement("button", {
    "aria-label": "Retry this bill",
    className: classes.iconButton,
    disabled: !!retryBlockedReason,
    onClick: () => setIsRetrying(true),
    title: retryBlockedReason || 'Retry this bill',
    type: "button"
  }, /*#__PURE__*/React.createElement(Icon, {
    state: "grey",
    type: "reload"
  })), /*#__PURE__*/React.createElement("button", {
    "aria-label": "Delete this bill",
    className: classes.iconButton,
    disabled: !canDelete,
    onClick: () => setIsConfirmingDelete(true),
    title: canDelete ? 'Delete this bill' : isMissing ? 'This bill is no longer available' : deleteBlockedReason,
    type: "button"
  }, /*#__PURE__*/React.createElement(Icon, {
    state: "grey",
    type: "trash"
  })), /*#__PURE__*/React.createElement("button", {
    "aria-label": "Close",
    className: classes.iconButton,
    onClick: handleClose,
    type: "button"
  }, /*#__PURE__*/React.createElement(Icon, {
    state: "grey",
    type: "close"
  })))), /*#__PURE__*/React.createElement("div", {
    className: classes.dataContent
  }, isMissing ? /*#__PURE__*/React.createElement("div", {
    className: classes.dataContentBlock
  }, /*#__PURE__*/React.createElement("div", {
    className: "".concat(bannerClasses.extractedBanner, " ").concat(bannerClasses.extractedBannerError, " ").concat(bannerClasses.extractedBannerLoadingOnly),
    role: "alert"
  }, /*#__PURE__*/React.createElement("div", {
    className: bannerClasses.extractedBannerContent
  }, /*#__PURE__*/React.createElement("span", {
    className: bannerClasses.extractedBannerAiIconWrap
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "ai-file-error"
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: bannerClasses.extractedBannerErrorTitle
  }, "This bill is no longer available"), /*#__PURE__*/React.createElement("div", {
    className: bannerClasses.extractedBannerText
  }, "It has been deleted, or the link points at a bill that was moved. Nothing was lost from the rest of your bills \u2014 close this to go back to the list."))))) : null, isMissing || !store.account.featureFlagsStore.getIsBillNotesEnabled ? null : /*#__PURE__*/React.createElement(BillNoteBlock, {
    note: bill.note || (listedBill === null || listedBill === void 0 ? void 0 : listedBill.note),
    noteBy: bill.note ? bill.noteBy : listedBill === null || listedBill === void 0 ? void 0 : listedBill.noteBy,
    noteAt: bill.note ? bill.noteAt : listedBill === null || listedBill === void 0 ? void 0 : listedBill.noteAt
  }), isProcessing ? /*#__PURE__*/React.createElement("div", {
    className: classes.dataContentBlock
  }, /*#__PURE__*/React.createElement("div", {
    "aria-busy": "true",
    "aria-live": "polite",
    className: "".concat(bannerClasses.extractedBanner, " ").concat(bannerClasses.extractedBannerLoadingOnly),
    role: "status"
  }, /*#__PURE__*/React.createElement("div", {
    className: bannerClasses.extractedBannerContent
  }, /*#__PURE__*/React.createElement("span", {
    className: bannerClasses.extractedBannerSpinnerWrap,
    "aria-hidden": true
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "ai-file-sparkle-animated"
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: bannerClasses.extractedBannerTitle
  }, "Processing your bill"), /*#__PURE__*/React.createElement("div", {
    className: bannerClasses.extractedBannerText
  }, "Please wait while we extract and validate the information from your uploaded file. This may take a moment."))))) : null, !isProcessing && bill.extractionSeconds !== null ? /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.dataContentBlock, " ").concat(classes.thinkingLine)
  }, "Thought for ", bill.extractionSeconds, " s") : null, showErrorBanner ? /*#__PURE__*/React.createElement("div", {
    className: classes.dataContentBlock
  }, /*#__PURE__*/React.createElement("div", {
    className: "".concat(bannerClasses.extractedBanner, " ").concat(bannerClasses.extractedBannerError, " ").concat(bannerClasses.extractedBannerLoadingOnly),
    role: "alert"
  }, /*#__PURE__*/React.createElement("div", {
    className: bannerClasses.extractedBannerContent
  }, /*#__PURE__*/React.createElement("span", {
    className: bannerClasses.extractedBannerAiIconWrap
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "ai-file-error"
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: bannerClasses.extractedBannerErrorTitle
  }, bannerCopy.title), /*#__PURE__*/React.createElement("div", {
    className: bannerClasses.extractedBannerErrorIntro
  }, bannerCopy.intro), reasonList.length > 0 ? /*#__PURE__*/React.createElement("ul", {
    className: bannerClasses.extractedBannerErrorList
  }, reasonList.map((msg, idx) => /*#__PURE__*/React.createElement("li", {
    key: idx,
    className: bannerClasses.extractedBannerErrorListItem
  }, msg))) : /*#__PURE__*/React.createElement("div", {
    className: bannerClasses.extractedBannerErrorIntro
  }, "No detailed message was returned. Please confirm the file and property setup, then try again or contact support if the issue continues."), handEntryNote ? /*#__PURE__*/React.createElement("div", {
    className: bannerClasses.extractedBannerErrorIntro
  }, handEntryNote) : null, /*#__PURE__*/React.createElement(Box, {
    sx: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px'
    }
  }, canEnterByHand && !isEnteringByHand ?
  /*#__PURE__*/
  // First: a retry spends a read, and for a read that was doubted it can land the same way.
  React.createElement(Button, {
    variant: "actionButtonInvert",
    onClick: () => setEnteringBillId(bill.billId)
  }, "Enter it by hand") : null, isDuplicate && duplicateOfBillId && onOpenBill ? /*#__PURE__*/React.createElement(Button, {
    variant: "actionButtonInvert",
    onClick: () => onOpenBill(duplicateOfBillId)
  }, "View the original bill") : null, copyOnRecordId && onOpenBill ?
  /*#__PURE__*/
  // Where a hand entry refused as a copy points: the bill already on record (LB-6095).
  React.createElement(Button, {
    variant: "actionButtonInvert",
    onClick: () => onOpenBill(copyOnRecordId)
  }, "Open the copy on record") : null, isHeldForPlan ?
  /*#__PURE__*/
  // Its way out is the plan: bill-automation refuses an attach, and reads it once upgraded.
  React.createElement(Needs, {
    tier: "Plus",
    where: "Bills page",
    className: classes.planNeeds
  }) : null, (isRejected || needsProperty) && !isHeldForPlan && onAttach ?
  /*#__PURE__*/
  // The remediation itself, where the problem is shown (LB-5766): attach picks
  // the property, and can allow the sender so the next bill needs nobody.
  React.createElement(Button, {
    variant: "actionButtonInvert",
    onClick: () => {
      handleClose();
      onAttach(bill.billId);
    }
  }, "Attach to a property") : null, (bill.status === BILL_AUTOMATION_STATUS.failed || needsProperty) && !retryBlockedReason ?
  /*#__PURE__*/
  // Named where the reasons name it ("Retry the bill…"), not only as the file
  // card's icon (LB-5918).
  React.createElement(Button, {
    variant: "actionButtonInvert",
    onClick: () => setIsRetrying(true)
  }, "Retry bill") : null))))) : null, showWarningBanner ? /*#__PURE__*/React.createElement("div", {
    className: classes.dataContentBlock
  }, /*#__PURE__*/React.createElement("div", {
    "aria-live": "polite",
    className: "".concat(bannerClasses.extractedBanner, " ").concat(bannerClasses.extractedBannerWarning, " ").concat(bannerClasses.extractedBannerLoadingOnly),
    role: "status"
  }, /*#__PURE__*/React.createElement("div", {
    className: bannerClasses.extractedBannerContent
  }, /*#__PURE__*/React.createElement("span", {
    className: bannerClasses.extractedBannerAiIconWrap
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "ai-file-warning"
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: bannerClasses.extractedBannerWarningTitle
  }, "Review the following warnings"), /*#__PURE__*/React.createElement("div", {
    className: bannerClasses.extractedBannerWarningIntro
  }, "We detected a few items that may need your attention:"), /*#__PURE__*/React.createElement("ul", {
    className: bannerClasses.extractedBannerWarningList
  }, warningList.map((msg, idx) => /*#__PURE__*/React.createElement("li", {
    key: idx,
    className: bannerClasses.extractedBannerWarningListItem
  }, msg))))))) : null, scheduledServices.length ? /*#__PURE__*/React.createElement("div", {
    className: classes.dataContentBlock
  }, /*#__PURE__*/React.createElement("div", {
    "aria-live": "polite",
    className: "".concat(bannerClasses.extractedBanner, " ").concat(bannerClasses.extractedBannerLoadingOnly),
    role: "status"
  }, /*#__PURE__*/React.createElement("div", {
    className: bannerClasses.extractedBannerContent
  }, /*#__PURE__*/React.createElement("span", {
    className: bannerClasses.extractedBannerAiIconWrap
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "calendar"
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: bannerClasses.extractedBannerTitle
  }, "Scheduled for ", scheduledMonths.join(' and ')), scheduledServices.map((utility, index) => /*#__PURE__*/React.createElement("div", {
    className: bannerClasses.extractedBannerText
    // Two services can share one property utility (water and sewer on "Water/Sewer").
    ,
    key: "".concat(utility.propertyId, "-").concat(utility.utilityId, "-").concat(index)
  }, [utility.serviceType, utility.propertyAddress].filter(Boolean).join(' at '), utility.scheduleReason ? ": ".concat(utility.scheduleReason) : '')), /*#__PURE__*/React.createElement("div", {
    className: bannerClasses.extractedBannerText
  }, scheduledMonths.length > 1 ? 'As each month opens, Apply Billing brings it in for you.' : 'When that month opens, Apply Billing brings it in for you.'), /*#__PURE__*/React.createElement(Box, {
    mt: 1
  }, /*#__PURE__*/React.createElement(Button, {
    onClick: () => setIsConfirmingUseThisCycle(true),
    size: "small",
    variant: "outlined"
  }, "Use it this cycle")))))) : null, isEnteringByHand ? /*#__PURE__*/React.createElement(BillDetailsCard, {
    billPropertyIds: bill.assignedPropertyIds,
    customerName: store.admin.companyName,
    data: bill.data,
    editTitle: "Enter this bill by hand",
    hideMatch: true
    // Keyed on the bill, so a form left open never carries over to the next bill opened.
    ,
    key: "hand-entry-".concat(bill.billId),
    onCancel: () => setEnteringBillId(null),
    onSave: handleEnterByHand,
    properties: properties,
    saveLabel: "Save and check",
    startEditing: true
  }) : null, extractedBillData ? /*#__PURE__*/React.createElement(React.Fragment, null, apGlAccounts !== null && apGlAccounts !== void 0 && apGlAccounts.isEnabled ? /*#__PURE__*/React.createElement(SectionHeading, {
    caption: "What goes to AppFolio: each line item and its GL account.",
    title: "Accounts payable"
  }) : /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.dataContentBlock, " ").concat(classes.introLine)
  }, "Extraction complete. Here's what we found:"), /*#__PURE__*/React.createElement(BillDetailsCard, {
    apGlAccounts: apGlAccounts,
    billPropertyIds: bill.assignedPropertyIds,
    customerName: store.admin.companyName,
    data: extractedBillData,
    editBlockedReason: editBlockedReason,
    isAppfolio: isAppfolio,
    isInvoiceNumberLocked: isInvoiceNumberLocked,
    needsProperty: needsProperty,
    hideMatch: !!bill.enteredByHandAt,
    onPush: () => appfolioPush.handlePush(bill.billId, apPushInvoiceNumber.invoiceNumber),
    onSave: handleSaveBillData,
    properties: properties,
    pushRefusalCode: appfolioPush.getRefusalCode(bill.billId),
    pushState: pushState
    // A payable needs the property the bill is for.
    ,
    showPush: canTriggerPush && !needsProperty
  }), /*#__PURE__*/React.createElement(BillEditHistoryCard, {
    bill: bill
    // A bill entered by hand has no read to go back to (LB-6095).
    ,
    canRestore: !editBlockedReason && bill.isEdited && !bill.enteredByHandAt,
    onRestore: handleRestoreBillData
  }), needsProperty ? null : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SectionHeading, {
    caption: "How the charges are split onto the property's allocation tables. We've prepared one for each utility: review and approve the charges.",
    title: "Allocation"
  }), /*#__PURE__*/React.createElement(AllocationSuggestionsCard, {
    billId: bill.billId,
    key: "allocations-".concat(bill.billId, "-").concat(allocationsVersion),
    currency: extractedBillData.currency,
    data: extractedBillData,
    monthInProgress: monthInProgress,
    onSaveSettings: handleSaveAllocationSettings,
    properties: properties,
    propertyIds: bill.assignedPropertyIds,
    settings: bill.allocationSettings
  }))) : null))), /*#__PURE__*/React.createElement(RetryBillModal, {
    handleClose: () => setIsRetrying(false),
    isOpen: isRetrying,
    onRetried: handleRetried,
    row: isRetrying ? {
      id: bill.billId,
      fileName,
      allocationCount
    } : null
  }), /*#__PURE__*/React.createElement(ConfirmActionModal, {
    confirmLabel: "Use it this cycle",
    isOpen: isConfirmingUseThisCycle,
    message: "".concat(fileName, " is scheduled for ").concat(scheduledMonths.join(' and '), ". Used this cycle, it is billed in the month in progress, and any earlier period its table was meant for stays unbilled unless you enter it yourself."),
    onClose: () => setIsConfirmingUseThisCycle(false),
    onConfirm: handleUseThisCycle,
    title: "Use this bill this cycle?"
  }), /*#__PURE__*/React.createElement(ConfirmActionModal, {
    confirmLabel: "Delete bill",
    isOpen: isConfirmingDelete,
    message: getDeleteConfirmation({
      fileName,
      allocationCount
    }),
    onClose: () => setIsConfirmingDelete(false),
    onConfirm: handleDelete,
    title: "Delete bill?"
  }));
});